'use client'

import React, { useCallback, useMemo, useState } from 'react'
import { useLocale } from 'next-intl'

import {
    HOLIDAY_PACKAGE_SORT,
    type HolidayPackageSort,
    type HolidayRegion
} from '@/src/constants/holidays'
import type { HolidayPackageGridSection } from '@/src/types/holidays'
import useClevertapForHolidays from '@/src/hooks/analytics/clevertap/holidays'
import { HOLIDAY_ACTION, HOLIDAY_ACTION_TYPE } from '@/src/utils/analytics/holidays/holidayEvents'
import { buildHolidayRegionSummaries, filterHolidayPackagesByRegion } from '@/src/utils/holidays/holidayRegions'
import { sortHolidayPackages } from '@/src/utils/holidays/sortHolidayPackages'

import HolidaySection from '../holidaySection/holidaySection'
import PackageSortControlWrapper from '../packageSortControl'
import RegionFilterWrapper from '../regionFilter'

import PackageGrid from './packageGrid'

interface PackageGridWrapperProps {
    section: HolidayPackageGridSection
    collectionSlug: string
    isMobile: boolean
    variant: 'card' | 'plain' | 'flat'
    isFirst: boolean
}

/** A single region would make the filter a no-op; the sort still shows on its own. */
const MIN_REGIONS_FOR_FILTER = 2

const PackageGridWrapper: React.FC<PackageGridWrapperProps> = ({
    section,
    collectionSlug,
    isMobile,
    variant,
    isFirst
}) => {
    const locale = useLocale()
    const { triggerHolidayUIActionEvent } = useClevertapForHolidays()
    const [sort, setSort] = useState<HolidayPackageSort>(HOLIDAY_PACKAGE_SORT.PRICE_LOW_TO_HIGH)
    const [selectedRegions, setSelectedRegions] = useState<HolidayRegion[]>([])

    const allPackages = useMemo(() => section.packages ?? [], [section.packages])
    const regionSummaries = useMemo(() => buildHolidayRegionSummaries(allPackages), [allPackages])
    const sortedPackages = useMemo(() => sortHolidayPackages(allPackages, sort, locale), [allPackages, sort, locale])

    const visiblePackages = useMemo(
        () => (selectedRegions.length ? filterHolidayPackagesByRegion(sortedPackages, selectedRegions) : sortedPackages),
        [selectedRegions, sortedPackages]
    )

    const handleRegionToggle = useCallback(
        (region: HolidayRegion) => {
            const isSelected = selectedRegions.includes(region)
            const next = isSelected ? selectedRegions.filter(item => item !== region) : [...selectedRegions, region]
            setSelectedRegions(next)
            triggerHolidayUIActionEvent(HOLIDAY_ACTION.CONTENT_ENGAGED, {
                action_type: HOLIDAY_ACTION_TYPE.CONTENT,
                component: 'region_filter',
                engagement_type: isSelected ? 'deselect' : 'select',
                component_label: region,
                selected_regions: next.join(',') || 'N/A'
            })
        },
        [selectedRegions, triggerHolidayUIActionEvent]
    )

    const handleRegionClear = useCallback(() => {
        setSelectedRegions([])
        triggerHolidayUIActionEvent(HOLIDAY_ACTION.CONTENT_ENGAGED, {
            action_type: HOLIDAY_ACTION_TYPE.CONTENT,
            component: 'region_filter',
            engagement_type: 'clear'
        })
    }, [triggerHolidayUIActionEvent])

    if (!allPackages.length) return null

    return (
        <HolidaySection
            id={section.id}
            title={section.title}
            isMobile={isMobile}
            variant={variant}
            isFirst={isFirst}
            beforeTitle={
                <RegionFilterWrapper
                    summaries={regionSummaries.length >= MIN_REGIONS_FOR_FILTER ? regionSummaries : []}
                    selected={selectedRegions}
                    onToggle={handleRegionToggle}
                    onClear={handleRegionClear}
                    action={<PackageSortControlWrapper value={sort} onChange={setSort} isMobile={isMobile} />}
                    isMobile={isMobile}
                />
            }
        >
            <PackageGrid
                packages={visiblePackages}
                collectionSlug={collectionSlug}
                sourceSection={section.id}
                isMobile={isMobile}
            />
        </HolidaySection>
    )
}

export { PackageGridWrapper }
export default PackageGridWrapper
